import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Pencil, Save } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Badge } from "@/components/Badge/Badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useDiagnosticAttempt } from "../../hooks/useDiagnosticAttempt";
import {
  authoredScoreCeiling,
  scoreDiagnostic,
} from "../../utils/diagnosticScoring";
import { bandLabels } from "../../utils/stages";
import {
  FALLBACK_TRADE_OPTIONS,
  hasTradeSpecificQuestions,
  isQuestionApplicable,
  toggleTrade,
  tradeLabels,
} from "../../utils/trades";
import { DownloadReportButton } from "../../pdf/DownloadReportButton";
import { ResultRail } from "../ResultRail/ResultRail";
import { DiagnosticLoadFailed } from "../DiagnosticLoadFailed";
import { DiagnosticNotFound } from "../DiagnosticNotFound";
import { DiagnosticFillSkeleton } from "../DiagnosticSkeletons";
import { parseRecordId } from "../../utils/routeParams";
import { QuestionField } from "./QuestionField";
import { TradeSelector } from "./TradeSelector";
import type { BuildReportInput } from "../../pdf/diagnosticReportData";
import type {
  DiagnosticResponseRow,
  DiagnosticTradeOption,
  SaveCoachResponsesPayload,
} from "../../types/diagnostic";

/**
 * The fill screen: the instrument, the answers, and a live placement rail.
 *
 * There is no autosave and no debounce — every write is an explicit button
 * press, which is what lets a coach work through the form in front of a
 * business owner without the page changing under them. A completed attempt is
 * read-only until "Edit answers" is pressed.
 */
export const ReviewScreen: React.FC = () => {
  const navigate = useNavigate();
  const { attemptId: rawId } = useParams<{ attemptId: string }>();
  // `null` for a junk segment. The hook refuses a non-finite id, so passing
  // `NaN` through left the page on a skeleton forever.
  const attemptId = parseRecordId(rawId);
  const {
    attempt,
    loading,
    saving,
    completing,
    hydrationKey,
    reload,
    save,
    saveAndComplete,
    saveCorrection,
  } = useDiagnosticAttempt(attemptId ?? NaN);

  const [ordinalAnswers, setOrdinalAnswers] = useState<Record<number, number>>(
    {},
  );
  const [yesNoAnswers, setYesNoAnswers] = useState<Record<number, boolean>>({});
  const [textAnswers, setTextAnswers] = useState<Record<number, string>>({});
  const [notes, setNotes] = useState("");
  /**
   * Trades the business runs. Held locally so toggling one re-renders the form
   * immediately; it is persisted with the next save alongside the answers.
   */
  const [trades, setTrades] = useState<string[]>([]);
  /** Completed attempts are read-only until the coach explicitly edits. */
  const [editing, setEditing] = useState(false);

  // Question cards, so answering one can scroll the next into view.
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const registerQuestionRef = useCallback(
    (questionId: number) => (el: HTMLDivElement | null) => {
      questionRefs.current[questionId] = el;
    },
    [],
  );

  /**
   * Seed the form from the stored attempt.
   *
   * Keyed on `hydrationKey`, which only advances on a fetch — never on a save
   * response. Rehydrating whenever the attempt object changed would revert
   * anything typed while a save was in flight, with no dirty tracking to
   * notice it had happened.
   */
  useEffect(() => {
    if (!attempt) return;
    const ordinal: Record<number, number> = {};
    const yesNo: Record<number, boolean> = {};
    const text: Record<number, string> = {};
    for (const response of attempt.responses) {
      if (response.ordinalChoiceId !== null) {
        ordinal[response.questionId] = response.ordinalChoiceId;
      }
      if (response.yesNoValue !== null) {
        yesNo[response.questionId] = response.yesNoValue;
      }
      if (response.textValue !== null) {
        text[response.questionId] = response.textValue;
      }
    }
    setOrdinalAnswers(ordinal);
    setYesNoAnswers(yesNo);
    setTextAnswers(text);
    setNotes(attempt.coachNotes ?? "");
    setTrades(attempt.trades);
    // Re-seeded with the answers, not held across them. This was the one piece
    // of the form's state that survived a change of `attemptId`, so opting
    // into correcting one completed diagnostic opened the next one already
    // unlocked — a screen that is meant to be read-only until asked, with an
    // audit trail one keystroke away. A reload after a correction re-seeds it
    // too, which is right: the correction has been committed.
    setEditing(false);
    // `attempt` is deliberately absent from the dependency list — see above.
    // It and `hydrationKey` are set in the same state update, so the attempt
    // read here is always the one that fetch just delivered.
  }, [hydrationKey]);

  const sections = useMemo(
    () =>
      attempt
        ? [...attempt.version.sections].sort(
            (a, b) => a.orderNumber - b.orderNumber,
          )
        : [],
    [attempt],
  );

  const tradeOptions: DiagnosticTradeOption[] = attempt?.availableTrades.length
    ? attempt.availableTrades
    : FALLBACK_TRADE_OPTIONS;

  /** Does this diagnostic ask anything trade-specific? Older ones do not. */
  const isTradeAware = useMemo(
    () => hasTradeSpecificQuestions(sections),
    [sections],
  );

  /**
   * The questions this business is actually asked. Everything that counts —
   * progress, completion, the live placement — derives from this rather than
   * from the full authored set, so a trade the company does not run never
   * reads as unfinished work.
   */
  const visibleSections = useMemo(
    () =>
      sections
        .map((section) => ({
          ...section,
          questions: [...section.questions]
            .filter((q) => isQuestionApplicable(q, trades))
            .sort((a, b) => a.orderNumber - b.orderNumber),
        }))
        // A section whose every question belongs to an unselected trade has
        // nothing left to show.
        .filter((section) => section.questions.length > 0),
    [sections, trades],
  );

  const visibleQuestionIds = useMemo(
    () => new Set(visibleSections.flatMap((s) => s.questions.map((q) => q.id))),
    [visibleSections],
  );

  const totalQuestions = visibleQuestionIds.size;
  const answeredCount = useMemo(() => {
    let count = 0;
    for (const id of visibleQuestionIds) {
      if (
        ordinalAnswers[id] !== undefined ||
        yesNoAnswers[id] !== undefined ||
        (textAnswers[id] ?? "").trim() !== ""
      ) {
        count += 1;
      }
    }
    return count;
  }, [visibleQuestionIds, ordinalAnswers, yesNoAnswers, textAnswers]);

  const needsTrades = isTradeAware && trades.length === 0;
  const allAnswered =
    !needsTrades && totalQuestions > 0 && answeredCount === totalQuestions;

  const responseRows: DiagnosticResponseRow[] = useMemo(() => {
    const rows: DiagnosticResponseRow[] = [];
    let id = 1;
    // Answers for a trade that has since been deselected stay in local state,
    // so re-selecting the trade brings them back, but they are not sent and
    // not scored.
    for (const [questionId, choiceId] of Object.entries(ordinalAnswers)) {
      if (!visibleQuestionIds.has(Number(questionId))) continue;
      rows.push({
        id: id++,
        ordinalChoiceId: choiceId,
        questionId: Number(questionId),
        textValue: null,
        yesNoValue: null,
      });
    }
    for (const [questionId, value] of Object.entries(yesNoAnswers)) {
      if (!visibleQuestionIds.has(Number(questionId))) continue;
      rows.push({
        id: id++,
        ordinalChoiceId: null,
        questionId: Number(questionId),
        textValue: null,
        yesNoValue: value,
      });
    }
    for (const [questionId, value] of Object.entries(textAnswers)) {
      if (value.trim() === "") continue;
      if (!visibleQuestionIds.has(Number(questionId))) continue;
      rows.push({
        id: id++,
        ordinalChoiceId: null,
        questionId: Number(questionId),
        textValue: value,
        yesNoValue: null,
      });
    }
    return rows;
  }, [ordinalAnswers, yesNoAnswers, textAnswers, visibleQuestionIds]);

  const livePreview = useMemo(
    () =>
      attempt
        ? scoreDiagnostic({
            responses: responseRows,
            status: allAnswered ? "complete" : "in_progress",
            trades,
            version: attempt.version,
          })
        : null,
    [attempt, responseRows, allAnswered, trades],
  );

  const buildPayload = (): SaveCoachResponsesPayload => ({
    coachNotes: notes.trim() === "" ? null : notes,
    responses: responseRows.map((row) => ({
      questionId: row.questionId,
      ...(row.ordinalChoiceId != null
        ? { ordinalChoiceId: row.ordinalChoiceId }
        : {}),
      ...(row.yesNoValue != null ? { yesNoValue: row.yesNoValue } : {}),
      ...(row.textValue != null ? { textValue: row.textValue } : {}),
    })),
    trades,
  });

  /** Band labels in ascending order — the stage names for this diagnostic. */
  const stageNames = useMemo(
    () => (attempt ? bandLabels(attempt.version.bands) : []),
    [attempt],
  );

  /** Highest score any authored choice awards — the score bars' full scale. */
  const scoreCeiling = useMemo(
    () => authoredScoreCeiling(sections),
    [sections],
  );

  /**
   * Which sections set the ceiling rather than scoring. The result envelope
   * does not carry the flag, so the rail has to be told — the PDF reads the
   * same flag off the same tree for the same filter.
   */
  const gateSectionIds = useMemo(
    () => sections.filter((s) => s.isGate).map((s) => s.id),
    [sections],
  );

  // Gate questions must all be answered before a stage can be placed — but
  // only the ones this business is asked.
  const gateAnswered = useMemo(() => {
    const gateQuestions = visibleSections
      .filter((s) => s.isGate)
      .flatMap((s) => s.questions)
      .filter((q) => q.type === "ordinal_mc");
    return gateQuestions.every((q) => ordinalAnswers[q.id] !== undefined);
  }, [visibleSections, ordinalAnswers]);

  /** Flat question order, used to find "the next one" after an answer. */
  const questionOrder = useMemo(
    () => visibleSections.flatMap((s) => s.questions.map((q) => q.id)),
    [visibleSections],
  );

  /**
   * Advance to the next question so a coach can work straight down the page.
   * Only fires on a newly answered question — revising an earlier answer
   * should not yank the page away.
   */
  const advanceFrom = useCallback(
    (questionId: number) => {
      const next = questionOrder[questionOrder.indexOf(questionId) + 1];
      const target = next != null ? questionRefs.current[next] : null;
      if (!target) return;
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      window.requestAnimationFrame(() =>
        target.scrollIntoView({
          behavior: reduceMotion ? "auto" : "smooth",
          block: "center",
        }),
      );
    },
    [questionOrder],
  );

  if (attemptId === null) {
    return (
      <DiagnosticNotFound message="That is not a diagnostic we can look up." />
    );
  }
  // The shape the rest of the feature was brought round to: in flight, then
  // terminal. `attempt` is null only before the first payload arrives, so a
  // fetch that has settled without one has failed.
  if (loading && !attempt) {
    return <DiagnosticFillSkeleton />;
  }
  if (!attempt) {
    return (
      <MainPageWrapper title="Diagnostic">
        <DiagnosticLoadFailed
          message="That diagnostic could not be loaded."
          onRetry={reload}
        />
      </MainPageWrapper>
    );
  }

  const isAlreadyComplete = attempt.status === "complete";
  /**
   * An archived attempt is refused **every** write by the API — the draft save,
   * the completion and the correction alike. `CompanyDetail` withholds the Open
   * link on one for exactly that reason, but this route stays directly
   * reachable: browser Back after archiving used to land the coach on a fully
   * editable screen whose every save was rejected, and whatever had been typed
   * in the meantime was gone.
   *
   * So the refusal lives here too, and it is stated rather than implied — a
   * form with its buttons quietly removed reads as broken, not as archived.
   * Restoring is the way back in, in the same actions cell that archived it.
   */
  const isArchived = attempt.isArchived;
  // A finished attempt is read-only until the coach opts into correcting it;
  // an archived one is read-only outright, with nothing to opt into.
  const isReadOnly = isArchived || (isAlreadyComplete && !editing);
  // The stage the member was told, not one recomputed on read.
  const recordedStage = attempt.finalStageLabel ?? attempt.result.band?.label;

  /**
   * The report as this screen sees it: the coach's live, unsaved answers, so a
   * mid-session summary can be handed over before anything is committed.
   *
   * `recordedStageLabel` and `recordedOutcome` are only supplied once the
   * attempt is complete. While drafting there is nothing recorded and the live
   * derivation is the only truth; once complete the record wins even
   * mid-correction, because the printed artifact must agree with the one the
   * business already holds.
   *
   * The envelope follows the same rule as the answers it summarises. A
   * completed attempt nobody is editing has nothing live to show, and its
   * preview is not merely redundant but wrong: `livePreview` is scored as
   * `allAnswered ? "complete" : "in_progress"`, and `allAnswered` is false
   * whenever `needsTrades` — so a completed attempt recorded before the
   * `trades` column existed would have produced a PDF from a preview
   * reporting itself in progress, with no outcome and no placement.
   */
  const buildReport = (): BuildReportInput => ({
    diagnosticName: attempt.diagnosticName,
    versionNumber: attempt.version.versionNumber,
    subjectName: attempt.company.name,
    completedAt: attempt.completedAt,
    coachNotes: notes.trim() === "" ? null : notes,
    version: attempt.version,
    responses: responseRows,
    result: isReadOnly ? attempt.result : (livePreview ?? attempt.result),
    recordedStageLabel: isAlreadyComplete ? attempt.finalStageLabel : null,
    recordedOutcome: isAlreadyComplete ? attempt.finalOutcome : null,
    trades,
    // The server's list where it served one, so the printed trade names cannot
    // drift from the backend's own keys.
    tradeOptions,
  });

  let questionNumber = 0;

  return (
    <MainPageWrapper
      actions={
        <div className="flex items-center gap-1">
          <Button
            onClick={() => navigate(`/admin/diagnostics/${attempt.companyId}`)}
            size="sm"
            variant="ghost"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to company
          </Button>
          <DownloadReportButton label="Download PDF" report={buildReport} />
        </div>
      }
      sidebar={
        // Rendered once. Below the questions on narrow screens, beside them
        // from `md` up — duplicating it for the two layouts would put two live
        // copies of the placement in the accessibility tree. `md` matches the
        // breakpoint MainPageWrapper switches its sidebar container to a row
        // at; using `lg` here would leave the rail full-width inside a row
        // between 768–1023px.
        <aside className="order-last w-full px-4 py-4 md:order-none md:w-80 md:flex-shrink-0">
          <ResultRail
            answeredCount={answeredCount}
            bandLabels={stageNames}
            gateAnswered={gateAnswered}
            gateSectionIds={gateSectionIds}
            needsTrades={needsTrades}
            // A finished attempt nobody is editing has a record; the rail
            // reads it rather than re-deriving one, so the placement in the
            // rail, the badge above it and the PDF cannot disagree. The
            // moment the coach opts into correcting it, the rail goes live
            // again — that is the mid-correction disagreement D13 accepts.
            preview={isReadOnly ? attempt.result : livePreview}
            recordedOutcome={isReadOnly ? attempt.finalOutcome : null}
            recordedStageLabel={isReadOnly ? attempt.finalStageLabel : null}
            scoreCeiling={scoreCeiling}
            totalQuestions={totalQuestions}
            tradeNames={tradeLabels(trades, tradeOptions)}
          />
        </aside>
      }
      subtitle={attempt.company.name}
      title={attempt.diagnosticName}
    >
      {isArchived && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-800">
          <p className="text-gray-900 dark:text-white">
            This diagnostic is archived and cannot be edited.
          </p>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Its answers, notes and edit history are all kept. Restore it from{" "}
            <Link
              className="underline underline-offset-2 hover:text-gray-900 dark:hover:text-white"
              to={`/admin/diagnostics/${attempt.companyId}`}
            >
              this company&apos;s diagnostic history
            </Link>{" "}
            before making any change.
          </p>
        </div>
      )}

      {isAlreadyComplete && (
        <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 text-sm dark:border-slate-700 dark:bg-slate-800">
          <span className="text-gray-500 dark:text-gray-400">
            This diagnostic is complete.
          </span>
          {recordedStage && <Badge variant="grey">{recordedStage}</Badge>}
          {!editing && !isArchived && (
            <Button
              className="ml-auto"
              onClick={() => setEditing(true)}
              size="sm"
              variant="outline"
            >
              <Pencil className="mr-1 h-4 w-4" />
              Edit answers
            </Button>
          )}
        </div>
      )}

      <div className="space-y-6">
        {isTradeAware && (
          <TradeSelector
            isDisabled={isReadOnly}
            onToggle={(key) =>
              setTrades((prev) => toggleTrade(prev, key, tradeOptions))
            }
            options={tradeOptions}
            selected={trades}
          />
        )}

        {needsTrades && (
          <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-slate-600 dark:text-gray-400">
            Select the trades this business operates to begin. The questions
            below change with the answer — margins, tickets, and closing rates
            are read against each trade&apos;s own targets.
          </div>
        )}

        {visibleSections.map((section) => (
          <section
            className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            key={section.id}
          >
            <div className="mb-1 flex flex-wrap items-center gap-2">
              <h3 className="text-base font-normal text-gray-900 dark:text-white">
                {section.name}
              </h3>
              {section.isGate && <Badge variant="grey">Hard gate</Badge>}
              {section.isPrivate && <Badge variant="grey">Private</Badge>}
              {!section.isScored && !section.isGate && (
                <Badge variant="grey">Informational</Badge>
              )}
              {section.isScored && (
                <Badge variant="grey">{section.weight}% weight</Badge>
              )}
            </div>
            {section.description && (
              <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
                {section.description}
              </p>
            )}
            <div className="space-y-3">
              {section.questions.map((question) => {
                questionNumber += 1;
                return (
                  <QuestionField
                    isDisabled={isReadOnly}
                    key={question.id}
                    onFirstAnswer={advanceFrom}
                    ordinalAnswer={ordinalAnswers[question.id]}
                    position={questionNumber}
                    question={question}
                    registerRef={registerQuestionRef(question.id)}
                    setOrdinal={(questionId, choiceId) =>
                      setOrdinalAnswers((prev) => ({
                        ...prev,
                        [questionId]: choiceId,
                      }))
                    }
                    setText={(questionId, value) =>
                      setTextAnswers((prev) => ({
                        ...prev,
                        [questionId]: value,
                      }))
                    }
                    setYesNo={(questionId, value) =>
                      setYesNoAnswers((prev) => ({
                        ...prev,
                        [questionId]: value,
                      }))
                    }
                    textAnswer={textAnswers[question.id] ?? ""}
                    tradeOptions={tradeOptions}
                    yesNoAnswer={yesNoAnswers[question.id]}
                  />
                );
              })}
            </div>
          </section>
        ))}

        <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <label
            className="text-sm text-gray-900 dark:text-white"
            htmlFor="coach-notes"
          >
            Coach notes (optional)
          </label>
          <Textarea
            className="mt-2"
            disabled={isReadOnly}
            id="coach-notes"
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes recorded alongside this company's diagnostic."
            rows={4}
            value={notes}
          />
        </div>
      </div>

      {!isAlreadyComplete && !isArchived && (
        <div className="sticky bottom-0 mt-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 py-4 dark:border-slate-700 dark:bg-slate-950">
          <Button
            disabled={saving || completing}
            // The interceptor has already told the coach; this screen has
            // nothing to add and builds no error state of its own (D10).
            // Catching only stops the rejection surfacing as an unhandled
            // promise rejection.
            onClick={() => save(buildPayload()).catch(() => undefined)}
            variant="outline"
          >
            <Save className="mr-1 h-4 w-4" />
            {saving ? "Saving…" : "Save draft"}
          </Button>
          <Button
            disabled={!allAnswered || saving || completing}
            onClick={() =>
              saveAndComplete(buildPayload())
                .then(() => navigate(`/admin/diagnostics/${attempt.companyId}`))
                // Toasted by the interceptor; the coach stays on the form with
                // the answers still in hand. Catching only stops the
                // unhandled rejection (D10).
                .catch(() => undefined)
            }
          >
            <CheckCircle2 className="mr-1 h-4 w-4" />
            {completing
              ? "Completing…"
              : needsTrades
                ? "Complete — select a trade first"
                : allAnswered
                  ? "Complete"
                  : `Complete — ${answeredCount} of ${totalQuestions} answered`}
          </Button>
        </div>
      )}

      {/* `editing` cannot be set while archived — the button that sets it is
          withheld above — but the write footer is gated on the same flag as
          the others rather than relying on that, so no future path can reopen
          it. */}
      {isAlreadyComplete && editing && !isArchived && (
        <div className="sticky bottom-0 mt-6 flex flex-wrap items-center gap-3 border-t border-gray-200 bg-gray-50 py-4 dark:border-slate-700 dark:bg-slate-950">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Editing a completed diagnostic — every change is recorded in its
            history.
          </span>
          <div className="ml-auto flex items-center gap-3">
            <Button
              disabled={saving}
              onClick={() => {
                setEditing(false);
                // Drop unsaved edits by reloading the stored answers.
                reload();
              }}
              variant="ghost"
            >
              Cancel
            </Button>
            <Button
              disabled={saving}
              onClick={() =>
                saveCorrection(buildPayload())
                  .then(() => {
                    setEditing(false);
                    reload();
                  })
                  // A refused correction leaves the coach in edit mode with the
                  // changes intact, which is where they need to be. Catching
                  // only stops the unhandled rejection (D10).
                  .catch(() => undefined)
              }
            >
              <Save className="mr-1 h-4 w-4" />
              {saving ? "Saving…" : "Save correction"}
            </Button>
          </div>
        </div>
      )}
    </MainPageWrapper>
  );
};

export default ReviewScreen;
